import React, { useEffect, useMemo, useState, useRef } from 'react';
import { skusApi, brandsApi, downloadAuthed } from '../api';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import BulkAssignBrandModal from '../components/BulkAssignBrandModal';
import ImportProgressModal from '../components/ImportProgressModal';
import { Search, Download, Upload, Plus, Loader2, X, Pencil, Trash2, ArrowUpDown, Tag } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import CoolingFields from '../components/CoolingFields';

const DEFAULT_DRAFT = { sku: '', product: '', brand: '', category: 'Lip', subCategory: 'Lipstick', mrp: 0, pack: '', hsn: '3304', channel: 'OWN WEBSITE + EBO', status: 'Active', itemType: 'T', coolingDays: 60, coolingActive: true };
const fmtCoolingAt = (v) => (v ? new Date(/Z|[+-]\d\d:\d\d$/.test(v) ? v : `${v}Z`).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' }) : '');
const SORT_OPTIONS = [
  { key: 'sku-asc', label: 'SKU · A → Z' },
  { key: 'sku-desc', label: 'SKU · Z → A' },
  { key: 'mrp-asc', label: 'MRP · low → high' },
  { key: 'mrp-desc', label: 'MRP · high → low' },
  { key: 'category-asc', label: 'Category · A → Z' },
  { key: 'product-asc', label: 'Product · A → Z' },
];

export default function ItemMaster() {
  const { user, theme } = useAuth();
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCode, setEditingCode] = useState(null); // sku code of the row being edited (null → add mode)
  const [draft, setDraft] = useState(DEFAULT_DRAFT);
  const [msg, setMsg] = useState('');
  const fileRef = useRef();
  const [importState, setImportState] = useState({ open: false, phase: 'uploading', progress: 0, fileName: '', result: null, errorMsg: '' });

  // filters + sort
  const [brand, setBrand] = useState('all');
  const [category, setCategory] = useState('all');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState('sku-asc');

  const load = () => { setLoading(true); skusApi.list().then(setItems).finally(() => setLoading(false)); };
  useEffect(load, []);

  // Iter29: brand dropdown source of truth is the Brand Master, not the
  // union of brands already present on SKUs. Falls back to the union if
  // the master is empty (fresh install).
  const [brandMaster, setBrandMaster] = useState([]);
  useEffect(() => { brandsApi.list({ only_active: true }).then(setBrandMaster).catch(() => setBrandMaster([])); }, []);

  // ---------- selection + bulk delete ----------
  const [selected, setSelected] = useState(() => new Set());
  const toggleOne = (sku) => setSelected(prev => {
    const n = new Set(prev);
    n.has(sku) ? n.delete(sku) : n.add(sku);
    return n;
  });
  const clearSelection = () => setSelected(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [assignBrandOpen, setAssignBrandOpen] = useState(false);
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} SKU(s)? This can't be undone.`)) return;
    setBulkDeleting(true);
    try {
      const skus = Array.from(selected);
      const r = await skusApi.bulkDelete(skus);
      alert(`Deleted ${r.deleted} of ${r.requested} SKUs`);
      clearSelection();
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Bulk delete failed');
    } finally { setBulkDeleting(false); }
  };
  const bulkAssignBrand = async (brand) => {
    try {
      const skus = Array.from(selected);
      const r = await skusApi.bulkAssignBrand(skus, brand);
      setMsg(`Assigned brand "${brand || '— none —'}" to ${r.updated} of ${r.requested} SKU(s)`);
      setTimeout(() => setMsg(''), 4000);
      clearSelection();
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Bulk assign failed');
      throw e;
    }
  };

  const brandOptions = useMemo(() => [...new Set(items.map(s => s.brand).filter(Boolean))].sort(), [items]);
  const categoryOptions = useMemo(() => [...new Set(items.map(s => s.category).filter(Boolean))].sort(), [items]);

  const list = useMemo(() => {
    const qLower = q.trim().toLowerCase();
    let filtered = items.filter(s => {
      if (brand !== 'all' && s.brand !== brand) return false;
      if (category !== 'all' && s.category !== category) return false;
      if (status !== 'all' && (s.status || '') !== status) return false;
      if (qLower && !`${s.sku || ''} ${s.product || ''}`.toLowerCase().includes(qLower)) return false;
      return true;
    });
    const strCmp = (a, b, key, dir = 'asc') => {
      const av = (a[key] || '').toString().toLowerCase();
      const bv = (b[key] || '').toString().toLowerCase();
      return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    };
    switch (sort) {
      case 'sku-desc': filtered = [...filtered].sort((a, b) => strCmp(a, b, 'sku', 'desc')); break;
      case 'mrp-asc': filtered = [...filtered].sort((a, b) => (Number(a.mrp) || 0) - (Number(b.mrp) || 0)); break;
      case 'mrp-desc': filtered = [...filtered].sort((a, b) => (Number(b.mrp) || 0) - (Number(a.mrp) || 0)); break;
      case 'category-asc': filtered = [...filtered].sort((a, b) => strCmp(a, b, 'category', 'asc') || strCmp(a, b, 'sku', 'asc')); break;
      case 'product-asc': filtered = [...filtered].sort((a, b) => strCmp(a, b, 'product', 'asc')); break;
      default: filtered = [...filtered].sort((a, b) => strCmp(a, b, 'sku', 'asc'));
    }
    return filtered;
  }, [items, q, brand, category, status, sort]);

  const clearFilters = () => { setQ(''); setBrand('all'); setCategory('all'); setStatus('all'); setSort('sku-asc'); };
  const activeFilterCount = [q, brand !== 'all', category !== 'all', status !== 'all'].filter(Boolean).length;

  const openAdd = () => { setEditingCode(null); setDraft(DEFAULT_DRAFT); setShowModal(true); };
  const openEdit = (row) => { setEditingCode(row.sku); setDraft({ ...DEFAULT_DRAFT, ...row }); setShowModal(true); };

  const save = async () => {
    const coolingNum = draft.coolingDays === '' || draft.coolingDays === null ? null : Number(draft.coolingDays);
    try {
      if (editingCode) {
        const { sku, ...patch } = draft;
        await skusApi.update(editingCode, { ...patch, mrp: Number(draft.mrp), coolingDays: coolingNum });
      } else {
        await skusApi.add({ ...draft, mrp: Number(draft.mrp), coolingDays: coolingNum });
      }
      setShowModal(false); setDraft(DEFAULT_DRAFT); setEditingCode(null); load();
    } catch (e) { alert(e.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete SKU ${row.sku} — ${row.product}? This can’t be undone.`)) return;
    try { await skusApi.remove(row.sku); load(); }
    catch (e) { alert(e.response?.data?.detail || 'Delete failed'); }
  };

  const doImport = async (file) => {
    if (!file) return;
    setImportState({ open: true, phase: 'uploading', progress: 0, fileName: file.name, result: null, errorMsg: '' });
    try {
      const r = await skusApi.import(file, (evt) => {
        const total = evt.total || evt.loaded;
        if (total > 0) {
          setImportState(s => ({ ...s, progress: Math.round((evt.loaded / total) * 100) }));
        }
      });
      setImportState(s => ({ ...s, phase: 'done', progress: 100, result: r }));
      const errs = (r.errors || []).length;
      setMsg(errs === 0
        ? `Imported: ${r.inserted} inserted, ${r.updated} updated`
        : `Imported ${r.inserted + r.updated} rows — ${errs} row${errs === 1 ? '' : 's'} skipped, see details.`);
      load();
      setTimeout(() => setMsg(''), 6000);
    } catch (e) {
      setImportState(s => ({ ...s, phase: 'error', errorMsg: e.response?.data?.detail || 'Import failed' }));
    } finally {
      // Reset the file input so re-uploading the same file re-triggers onChange
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const canManage = ['SuperAdmin', 'Admin'].includes(user?.role);
  const canModifyOrDelete = user?.role === 'SuperAdmin';

  return (
    <div>
      <PageHeader
        title="Item master"
        subtitle={<span>{items.length} SKUs · catalogue is uploaded and managed via Excel</span>}
        actions={canManage && (
          <div className="flex items-center gap-2 flex-wrap">
            {canModifyOrDelete && selected.size > 0 && (
              <>
                <button
                  data-testid="item-bulk-assign-brand-btn"
                  onClick={() => setAssignBrandOpen(true)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E7E2D3] bg-white text-[13px] hover:bg-[#F7F4EA]"
                  title={`Assign brand to ${selected.size} selected SKU(s)`}
                >
                  <Tag size={13} /> Assign brand ({selected.size})
                </button>
                <button
                  data-testid="item-bulk-delete-btn"
                  onClick={bulkDelete}
                  disabled={bulkDeleting}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E7E2D3] bg-white text-[13px] hover:bg-[#F7F4EA] disabled:opacity-50"
                  style={{ color: '#B84A4A' }}
                  title={`Delete ${selected.size} selected SKU(s)`}
                >
                  {bulkDeleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  Delete selected ({selected.size})
                </button>
              </>
            )}
            <button data-testid="item-download-template-btn" onClick={() => downloadAuthed(skusApi.templateUrl(), 'item-master-template.xlsx')} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E7E2D3] bg-white text-[13px] hover:bg-[#F7F4EA]"><Download size={13}/> Download template</button>
            <input ref={fileRef} type="file" accept=".xlsx" onChange={(e) => doImport(e.target.files?.[0])} className="hidden" />
            <button data-testid="item-import-btn" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E7E2D3] bg-white text-[13px] hover:bg-[#F7F4EA]"><Upload size={13}/> Import Excel</button>
            <button data-testid="item-export-btn" onClick={() => downloadAuthed(skusApi.exportUrl(), 'item-master.xlsx')} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E7E2D3] bg-white text-[13px] hover:bg-[#F7F4EA]"><Download size={13}/> Download Excel</button>
            <button data-testid="item-add-btn" onClick={openAdd} className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-white text-[13px]" style={{ background: theme.primary }}><Plus size={13}/> Add SKU</button>
          </div>
        )}
      />

      {msg && <div className="mb-3 text-[13px] px-3 py-2 rounded bg-[#E1EEE4] text-[#2f6b41]">{msg}</div>}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7a6f5c]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search SKU or name…" className="fm-focus w-full border border-[#E7E2D3] rounded-md pl-9 pr-3 py-2 text-[13px] bg-white" data-testid="item-search-input" />
          </div>
          <select data-testid="item-filter-brand" value={brand} onChange={e => setBrand(e.target.value)} className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white">
            <option value="all">All brands</option>
            {brandOptions.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <select data-testid="item-filter-category" value={category} onChange={e => setCategory(e.target.value)} className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white">
            <option value="all">All categories</option>
            {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="item-filter-status" value={status} onChange={e => setStatus(e.target.value)} className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white">
            <option value="all">All statuses</option>
            <option value="Active">Active</option>
            <option value="DEACTIVE">DEACTIVE</option>
            <option value="Inactive">Inactive</option>
            <option value="LIQUIDATE">LIQUIDATE</option>
          </select>
          <div className="flex items-center gap-1 pl-1">
            <ArrowUpDown size={13} className="text-[#7a6f5c]" />
            <select data-testid="item-sort" value={sort} onChange={e => setSort(e.target.value)} className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13px] bg-white">
              {SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </div>
          {activeFilterCount > 0 && (
            <button data-testid="item-clear-filters" onClick={clearFilters} className="text-[12px] px-2 py-1 rounded border border-[#E7E2D3] bg-white hover:bg-[#F7F4EA] text-[#7a6f5c]">Clear filters · {activeFilterCount}</button>
          )}
        </div>
        <div className="text-[12px] text-[#7a6f5c] max-w-[420px]"><span className="font-semibold text-[#232323]">{list.length}</span> of {items.length} SKUs shown</div>
      </div>

      <div className="fm-card fm-scroll-x">
        {loading ? <div className="p-8 text-center"><Loader2 className="animate-spin inline" /></div> : (
          <table className="w-full text-[13.5px]" data-testid="item-table">
            <thead>
              <tr className="text-left text-[12px] text-[#7a6f5c] border-b border-[#E7E2D3] whitespace-nowrap">
                {canModifyOrDelete && (
                  <th className="pl-4 pr-1 py-3 font-medium w-8">
                    <input
                      type="checkbox"
                      data-testid="item-select-all-checkbox"
                      checked={list.length > 0 && list.every(s => selected.has(s.sku))}
                      onChange={() => {
                        const all = list.length > 0 && list.every(s => selected.has(s.sku));
                        setSelected(prev => {
                          const n = new Set(prev);
                          list.forEach(s => (all ? n.delete(s.sku) : n.add(s.sku)));
                          return n;
                        });
                      }}
                      disabled={list.length === 0}
                      className="cursor-pointer accent-[#9C3D54] disabled:opacity-40"
                    />
                  </th>
                )}
                <th className="px-4 py-3 font-medium">SKU</th>
                <th className="px-4 py-3 font-medium">Tester SKU</th>
                <th className="px-4 py-3 font-medium">Product</th>
                <th className="px-4 py-3 font-medium">Brand</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium text-right">MRP</th>
                <th className="px-4 py-3 font-medium">Pack</th>
                <th className="px-4 py-3 font-medium">HSN</th>
                <th className="px-4 py-3 font-medium">Channel</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Cooling period</th>
                {canModifyOrDelete && <th className="px-4 py-3 font-medium text-right pr-6">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && (
                <tr><td colSpan={canModifyOrDelete ? 14 : 12} className="px-5 py-10 text-center text-[#7a6f5c]">
                  {items.length === 0 ? 'No SKUs yet — import via Excel or click Add SKU.' : 'No SKUs match those filters.'}
                </td></tr>
              )}
              {list.map(s => (
                <tr key={s.sku} className="fm-row border-t border-[#EFEADC] align-top">
                  {canModifyOrDelete && (
                    <td className="pl-4 pr-1 py-3 w-8">
                      <input
                        type="checkbox"
                        data-testid={`item-select-checkbox-${s.sku}`}
                        checked={selected.has(s.sku)}
                        onChange={() => toggleOne(s.sku)}
                        className="cursor-pointer accent-[#9C3D54]"
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 font-mono text-[13px] whitespace-nowrap">{s.sku}</td>
                  <td className="px-4 py-3 font-mono text-[12.5px] text-[#7a6f5c] whitespace-nowrap" data-testid={`item-tester-sku-${s.sku}`}>{s.testerSku || `${s.sku}-T`}</td>
                  <td className="px-4 py-3 font-medium min-w-[220px]">{s.product}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{s.brand}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{s.category}/{s.subCategory}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">₹{s.mrp}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{s.pack}</td>
                  <td className="px-4 py-3 font-mono whitespace-nowrap">{s.hsn}</td>
                  <td className="px-4 py-3 text-[12px] whitespace-nowrap">{s.channel}</td>
                  <td className="px-4 py-3"><StatusBadge status={s.status} small /></td>
                  <td className="px-4 py-3 whitespace-nowrap" data-testid={`item-type-${s.sku}`}>{(s.itemType || 'T') === 'T' ? 'T · Tester' : 'NT'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-[12.5px]" data-testid={`item-cooling-${s.sku}`}>
                    {(s.itemType || 'T') !== 'T' ? '—' : s.coolingActive === false ? <span className="text-[#7a6f5c]">Off</span>
                      : (s.coolingDays === undefined || s.coolingDays === null) ? <span className="text-[#9b2c1f]">Not set</span> : `${s.coolingDays} days`}
                    {s.coolingUpdatedBy && <div className="text-[11px] text-[#7a6f5c]">{s.coolingUpdatedBy} · {fmtCoolingAt(s.coolingUpdatedAt)}</div>}
                  </td>
                  {canModifyOrDelete && (
                    <td className="px-4 py-3 pr-6 whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1.5">
                        <button data-testid={`item-edit-${s.sku}`} onClick={() => openEdit(s)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#E7E2D3] bg-white text-[12px] hover:bg-[#F7F4EA]" title="Modify"><Pencil size={12} /> Modify</button>
                        <button data-testid={`item-delete-${s.sku}`} onClick={() => remove(s)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#E7E2D3] bg-white text-[12px] hover:bg-[#F7F4EA]" style={{ color: '#B84A4A' }} title="Delete"><Trash2 size={12} /> Delete</button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>          <div className="fm-card p-5 w-full max-w-[560px]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-semibold text-[16px]" data-testid="item-modal-title">{editingCode ? `Modify SKU · ${editingCode}` : 'Add SKU'}</div>
              <button onClick={() => setShowModal(false)}><X size={16} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FieldInput label="SKU Code" value={draft.sku} onChange={v => setDraft({ ...draft, sku: v })} readOnly={!!editingCode} />
              <div>
                <label className="text-[12.5px] font-medium flex items-center gap-1.5">
                  Tester SKU
                  <span className="text-[10.5px] font-normal text-[#7a6f5c]">(auto)</span>
                </label>
                <input
                  data-testid="item-modal-tester-sku"
                  value={draft.sku ? (draft.sku.endsWith('-T') ? draft.sku : `${draft.sku}-T`) : ''}
                  readOnly
                  tabIndex={-1}
                  placeholder="Auto-generated from SKU Code"
                  className="fm-focus mt-1 w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-[#F7F5EF] text-[#7a6f5c] font-mono"
                />
              </div>
              <FieldInput label="MRP (₹)" value={draft.mrp} onChange={v => setDraft({ ...draft, mrp: v })} type="number" />
              <FieldInput label="Product" value={draft.product} onChange={v => setDraft({ ...draft, product: v })} full />
              <div>
                <label className="text-[12.5px] font-medium">Brand</label>
                <select
                  data-testid="item-modal-brand-select"
                  value={draft.brand}
                  onChange={e => setDraft({ ...draft, brand: e.target.value })}
                  className="fm-select fm-focus mt-1 w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white"
                >
                  <option value="">— No brand scope —</option>
                  {brandMaster.map(b => <option key={b.name} value={b.name}>{b.name}</option>)}
                </select>
              </div>
              <FieldInput label="Category" value={draft.category} onChange={v => setDraft({ ...draft, category: v })} />
              <FieldInput label="Sub Category" value={draft.subCategory} onChange={v => setDraft({ ...draft, subCategory: v })} />
              <FieldInput label="Pack size" value={draft.pack} onChange={v => setDraft({ ...draft, pack: v })} />
              <FieldInput label="HSN" value={draft.hsn} onChange={v => setDraft({ ...draft, hsn: v })} />
              <div>
                <label className="text-[12.5px] font-medium">Status</label>
                <select value={draft.status} onChange={e => setDraft({ ...draft, status: e.target.value })} className="fm-select fm-focus mt-1 w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white">
                  <option>Active</option><option>Inactive</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-[12.5px] font-medium">Sales Channel</label>
                <select value={draft.channel} onChange={e => setDraft({ ...draft, channel: e.target.value })} className="fm-select fm-focus mt-1 w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white">
                  <option>OWN WEBSITE + EBO</option><option>DMS</option><option>NOT YET LAUNCHED</option><option>NOT FOR SALE</option>
                </select>
              </div>
              <CoolingFields draft={draft} setDraft={setDraft} sku={editingCode} />
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowModal(false)} className="px-3 py-2 border border-[#E7E2D3] rounded-md text-[13.5px]">Cancel</button>
              <button data-testid="item-modal-save-btn" onClick={save} className="px-4 py-2 rounded-md text-white text-[13.5px] font-medium" style={{ background: theme.primary }}>{editingCode ? 'Save changes' : 'Add SKU'}</button>
            </div>
          </div>
        </div>
      )}

      <BulkAssignBrandModal
        open={assignBrandOpen}
        onClose={() => setAssignBrandOpen(false)}
        onSubmit={bulkAssignBrand}
        count={selected.size}
        entityLabel="SKU"
        primary={theme.primary}
      />

      <ImportProgressModal
        open={importState.open}
        onClose={() => setImportState(s => ({ ...s, open: false }))}
        title="Item Master import"
        phase={importState.phase}
        progress={importState.progress}
        fileName={importState.fileName}
        result={importState.result}
        errorMsg={importState.errorMsg}
      />
    </div>
  );
}

function FieldInput({ label, value, onChange, type = 'text', full, readOnly }) {
  return (
    <div className={full ? 'col-span-2' : ''}>
      <label className="text-[12.5px] font-medium">{label}</label>
      <input readOnly={readOnly} type={type} value={value} onChange={e => onChange(e.target.value)} className={`fm-focus mt-1 w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] ${readOnly ? 'bg-[#F5F1E7] text-[#7a6f5c]' : 'bg-white'}`} />
    </div>
  );
}
